import type {
  RelationshipPack,
  RelationshipSubtypeManifest,
} from '../../lib/diagram/relationship-manifest.types';
import type {
  RelationFacet,
  RelationLifecycleStatus,
} from '../../lib/graph/types';
import { isRelationPredicate } from '../../lib/graph/relationshipSemantics';
import type { FieldSensitivity } from '../fields/types/FieldManifest';
import type { SourceAuthorityType } from '../data-profiles/sourceAuthorityTypes';

export type RelationEndpointKind =
  | 'person'
  | 'position'
  | 'department'
  | 'team'
  | 'project'
  | 'role_profile'
  | 'work_context'
  | 'process'
  | 'activity'
  | 'control'
  | 'system'
  | 'objective'
  | 'initiative'
  | 'queue'
  | 'skill'
  | 'credential';

export type RelationContractValidationCode =
  | 'alias_used_as_canonical'
  | 'unregistered_canonical_predicate'
  | 'incomplete_registry_definition'
  | 'invalid_relationship_source_type'
  | 'unsupported_relationship_qualifier'
  | 'invalid_relationship_subject_kind'
  | 'invalid_relationship_object_kind';

export interface RelationContractValidationIssue {
  readonly code: RelationContractValidationCode;
  readonly message: string;
}

export interface RelationContractValidationInput {
  readonly predicate: string;
  readonly subjectKind?: string | null;
  readonly objectKind?: string | null;
  readonly qualifierKeys?: readonly string[];
  readonly sourceTypes?: readonly SourceAuthorityType[];
  /** Unknown/profile-only predicates remain non-canonical unless this boundary requires promotion. */
  readonly requireCanonical?: boolean;
  /** Canonical persistence boundaries can require every contract dimension to be registered. */
  readonly requireCompleteDefinition?: boolean;
}

export interface RelationContractValidationResult {
  readonly canonical: boolean;
  readonly lifecycle: RelationLifecycleStatus;
  readonly definition?: RelationPredicateDefinition;
  readonly issues: readonly RelationContractValidationIssue[];
}

export type RelationFamilyId =
  | 'identity_federation'
  | 'provenance_support'
  | 'capacity_responsibility'
  | 'process_compliance'
  | 'blueprint_infra'
  | 'strategy_planning'
  | 'software_design';

export interface RelationPredicateDefinition {
  readonly predicate: string;
  readonly family: RelationFamilyId;
  readonly lifecycle: RelationLifecycleStatus;
  readonly facets?: readonly RelationFacet[];
  readonly qualifierKeys?: readonly string[];
  readonly aliases?: readonly string[];
  readonly description?: string;
  /** Sensitivity of learning that the edge exists. */
  readonly existenceSensitivity?: FieldSensitivity;
  /** Per-qualifier classification; unspecified qualifiers inherit existence sensitivity. */
  readonly qualifierSensitivity?: Readonly<Record<string, FieldSensitivity>>;
  /** Registered ontology endpoints and authority classification for canonical runtime facts. */
  readonly subjectKinds?: readonly RelationEndpointKind[];
  readonly objectKinds?: readonly RelationEndpointKind[];
  readonly sourceTypes?: readonly SourceAuthorityType[];
}

export interface RelationPredicatePack {
  readonly id: string;
  readonly version: string;
  readonly definitions: readonly RelationPredicateDefinition[];
}

export type RelationPackValidationSeverity = 'error' | 'warning';

export type RelationPackValidationCode =
  | 'invalid_predicate'
  | 'unregistered_promoted_predicate'
  | 'unregistered_profile_predicate'
  | 'alias_used_as_canonical'
  | 'lifecycle_exceeds_registry'
  | 'deprecated_predicate'
  | 'facet_outside_registry'
  | 'qualifier_outside_registry';

export interface RelationPackValidationIssue {
  readonly severity: RelationPackValidationSeverity;
  readonly code: RelationPackValidationCode;
  readonly packId: string;
  readonly subtypeId?: string;
  readonly predicate?: string;
  readonly message: string;
}

export interface RelationPackValidationOptions {
  readonly requireRegisteredStatuses?: readonly RelationLifecycleStatus[];
  readonly reportUnregisteredProfileOnly?: boolean;
  readonly reportFacetDrift?: boolean;
  readonly reportQualifierDrift?: boolean;
}

const STRICT_STATUSES: readonly RelationLifecycleStatus[] = [
  'exchange_only',
  'promotable',
  'projection_enabled',
  'deprecated',
];

const LIFECYCLE_RANK: Record<RelationLifecycleStatus, number> = {
  draft: 0,
  profile_only: 1,
  exchange_only: 2,
  promotable: 3,
  projection_enabled: 4,
  deprecated: 5,
};

export const SHARED_RELATION_PREDICATE_PACK: RelationPredicatePack = {
  id: 'shared-relation-predicates',
  version: '2026.07.16.1',
  definitions: [
    {
      predicate: 'same_as',
      family: 'identity_federation',
      lifecycle: 'projection_enabled',
      facets: ['identity'],
      aliases: ['duplicate_of', 'equivalent_to'],
    },
    {
      predicate: 'external_reference',
      family: 'identity_federation',
      lifecycle: 'exchange_only',
      facets: ['identity', 'provenance'],
      qualifierKeys: ['sourceRef', 'confidence'],
      aliases: ['source_ref', 'external_ref'],
    },
    {
      predicate: 'supports_fact',
      family: 'provenance_support',
      lifecycle: 'exchange_only',
      facets: ['evidence_support', 'provenance'],
      qualifierKeys: ['sourceRef', 'confidence', 'validFrom', 'validTo'],
      aliases: ['logs_to', 'documents', 'cites'],
    },
    {
      predicate: 'evidenced_by',
      family: 'provenance_support',
      lifecycle: 'promotable',
      facets: ['evidence_support'],
      qualifierKeys: ['sourceRef', 'confidence'],
    },
    {
      predicate: 'capacity_commitment',
      family: 'capacity_responsibility',
      lifecycle: 'projection_enabled',
      facets: ['capacity_commitment'],
      qualifierKeys: ['allocation', 'effort', 'effortUnit', 'basis', 'role', 'targetKind', 'facet', 'commitmentMode', 'parentCommitmentRef', 'distributionComplete', 'validFrom', 'validTo', 'sensitivityTier'],
      aliases: ['assigned_to', 'works_on', 'allocated_to'],
      existenceSensitivity: 'manager',
      qualifierSensitivity: { allocation: 'manager', effort: 'manager', role: 'internal', sensitivityTier: 'hr_restricted' },
      subjectKinds: ['person', 'position', 'team'],
      objectKinds: ['team', 'project', 'work_context', 'process', 'activity', 'system', 'objective', 'initiative', 'queue'],
      sourceTypes: ['capacity_commitment'],
    },
    {
      predicate: 'project_assignment',
      family: 'capacity_responsibility',
      lifecycle: 'projection_enabled',
      facets: ['capacity_commitment'],
      qualifierKeys: ['allocation', 'effort', 'effortUnit', 'basis', 'validFrom', 'validTo', 'sensitivityTier'],
      aliases: ['project_allocation'],
      existenceSensitivity: 'manager',
      qualifierSensitivity: { allocation: 'manager', effort: 'manager', sensitivityTier: 'hr_restricted' },
      subjectKinds: ['person', 'position', 'team'],
      objectKinds: ['project'],
      sourceTypes: ['project_allocation'],
    },
    {
      predicate: 'responsible_for',
      family: 'capacity_responsibility',
      lifecycle: 'projection_enabled',
      facets: ['responsibility'],
      qualifierKeys: ['role', 'assignmentType', 'basis', 'validFrom', 'validTo'],
      subjectKinds: ['person', 'position', 'team'],
      objectKinds: ['project', 'work_context', 'process', 'activity', 'control', 'system', 'objective', 'initiative'],
      sourceTypes: ['project_allocation', 'capacity_commitment', 'process_inventory', 'process_responsibility'],
    },
    {
      predicate: 'accountable_for',
      family: 'capacity_responsibility',
      lifecycle: 'projection_enabled',
      facets: ['responsibility', 'governance'],
      qualifierKeys: ['role', 'validFrom', 'validTo'],
    },
    {
      predicate: 'consulted_on',
      family: 'capacity_responsibility',
      lifecycle: 'promotable',
      facets: ['responsibility'],
    },
    {
      predicate: 'informed_of',
      family: 'capacity_responsibility',
      lifecycle: 'promotable',
      facets: ['responsibility'],
    },
    {
      predicate: 'reports_to',
      family: 'capacity_responsibility',
      lifecycle: 'projection_enabled',
      qualifierKeys: ['validFrom', 'validTo', 'reportingLineType', 'basis'],
      existenceSensitivity: 'internal',
      subjectKinds: ['position'],
      objectKinds: ['position'],
      sourceTypes: ['position_structure'],
    },
    {
      predicate: 'matrix_reports_to',
      family: 'capacity_responsibility',
      lifecycle: 'projection_enabled',
      qualifierKeys: ['validFrom', 'validTo', 'reportingLineType', 'basis'],
      existenceSensitivity: 'internal',
      subjectKinds: ['position'],
      objectKinds: ['position'],
      sourceTypes: ['position_structure'],
    },
    {
      predicate: 'member_of',
      family: 'capacity_responsibility',
      lifecycle: 'projection_enabled',
      qualifierKeys: ['groupKind', 'role', 'roleProfileId', 'membershipType', 'validFrom', 'validTo', 'status', 'sensitivityTier'],
      existenceSensitivity: 'internal',
      qualifierSensitivity: { role: 'internal', roleProfileId: 'internal', sensitivityTier: 'hr_restricted' },
      subjectKinds: ['person', 'position'],
      objectKinds: ['team', 'project'],
      sourceTypes: ['team_membership'],
    },
    {
      predicate: 'requires_capacity',
      family: 'capacity_responsibility',
      lifecycle: 'projection_enabled',
      facets: ['capacity_commitment'],
      qualifierKeys: ['role', 'roleProfileId', 'skillRef', 'requiredFte', 'allocation', 'effort', 'effortUnit', 'basis', 'facet', 'validFrom', 'validTo', 'confidence', 'location', 'sensitivityTier'],
      aliases: ['capacity_requirement', 'workforce_demand'],
      existenceSensitivity: 'manager',
      qualifierSensitivity: { requiredFte: 'manager', allocation: 'manager', effort: 'manager', confidence: 'manager', sensitivityTier: 'hr_restricted' },
      subjectKinds: ['team', 'project', 'position', 'work_context', 'process', 'activity', 'objective', 'initiative'],
      objectKinds: ['position', 'role_profile'],
      sourceTypes: ['workforce_demand'],
    },
    {
      predicate: 'has_skill',
      family: 'capacity_responsibility',
      lifecycle: 'projection_enabled',
      facets: ['capability'],
      qualifierKeys: ['sourceProficiency', 'sourceScale', 'normalizedProficiency', 'scaleMappingStatus', 'evidenceRef', 'assessedAt', 'expiresAt', 'coverageComplete', 'confidence', 'sensitivityTier'],
      existenceSensitivity: 'manager',
      qualifierSensitivity: {
        sourceProficiency: 'manager',
        normalizedProficiency: 'manager',
        evidenceRef: 'manager',
        assessedAt: 'manager',
        confidence: 'manager',
        sensitivityTier: 'hr_restricted',
      },
      subjectKinds: ['person', 'position'],
      objectKinds: ['skill'],
      sourceTypes: ['skill_assertion'],
    },
    {
      predicate: 'requires_skill',
      family: 'capacity_responsibility',
      lifecycle: 'projection_enabled',
      facets: ['capability'],
      qualifierKeys: ['sourceProficiency', 'sourceScale', 'normalizedProficiency', 'scaleMappingStatus', 'required', 'importance', 'validFrom', 'validTo', 'confidence', 'sensitivityTier'],
      existenceSensitivity: 'internal',
      qualifierSensitivity: { sourceProficiency: 'manager', normalizedProficiency: 'manager', confidence: 'manager', sensitivityTier: 'hr_restricted' },
      subjectKinds: ['position', 'team', 'project', 'role_profile', 'work_context', 'process', 'activity', 'control'],
      objectKinds: ['skill'],
      sourceTypes: ['skill_requirement'],
    },
    {
      predicate: 'holds_credential',
      family: 'capacity_responsibility',
      lifecycle: 'projection_enabled',
      facets: ['capability', 'evidence_support'],
      qualifierKeys: ['credentialId', 'issuer', 'issuedAt', 'expiresAt', 'verificationStatus', 'evidenceRef', 'confidence', 'sensitivityTier'],
      existenceSensitivity: 'manager',
      qualifierSensitivity: { credentialId: 'manager', issuer: 'manager', verificationStatus: 'manager', evidenceRef: 'manager', sensitivityTier: 'hr_restricted' },
      subjectKinds: ['person', 'position'],
      objectKinds: ['credential'],
      sourceTypes: ['credential_assertion'],
    },
    {
      predicate: 'requires_credential',
      family: 'capacity_responsibility',
      lifecycle: 'projection_enabled',
      facets: ['capability'],
      qualifierKeys: ['required', 'validFrom', 'validTo', 'confidence', 'sensitivityTier'],
      existenceSensitivity: 'internal',
      qualifierSensitivity: { confidence: 'manager', sensitivityTier: 'hr_restricted' },
      subjectKinds: ['position', 'team', 'project', 'role_profile', 'work_context', 'process', 'activity', 'control'],
      objectKinds: ['credential'],
      sourceTypes: ['skill_requirement'],
    },
    {
      predicate: 'occupies',
      family: 'capacity_responsibility',
      lifecycle: 'projection_enabled',
      qualifierKeys: ['validFrom', 'validTo', 'acting', 'assignmentType', 'occupancyType', 'isPrimary', 'basis'],
      existenceSensitivity: 'internal',
      qualifierSensitivity: { acting: 'manager', assignmentType: 'internal', occupancyType: 'internal', isPrimary: 'internal' },
      subjectKinds: ['person'],
      objectKinds: ['position'],
      sourceTypes: ['position_structure'],
    },
    {
      predicate: 'owns',
      family: 'capacity_responsibility',
      lifecycle: 'promotable',
      facets: ['ownership', 'responsibility'],
      qualifierKeys: ['role', 'validFrom', 'validTo'],
    },
    {
      predicate: 'successor_of',
      family: 'capacity_responsibility',
      lifecycle: 'projection_enabled',
      facets: ['lifecycle'],
      qualifierKeys: [
        'validFrom',
        'validTo',
        'readiness',
        'rank',
        'emergencyCover',
        'developmentPlan',
        'criticalExperience',
        'assessedAt',
        'assessedBy',
        'sensitivityTier',
      ],
      existenceSensitivity: 'hr_restricted',
      qualifierSensitivity: {
        readiness: 'hr_restricted',
        rank: 'hr_restricted',
        emergencyCover: 'hr_restricted',
        developmentPlan: 'hr_restricted',
        criticalExperience: 'hr_restricted',
        assessedBy: 'hr_restricted',
        sensitivityTier: 'hr_restricted',
      },
      subjectKinds: ['person'],
      objectKinds: ['position'],
      sourceTypes: ['succession_plan'],
    },
    {
      predicate: 'controlled_by',
      family: 'process_compliance',
      lifecycle: 'projection_enabled',
      facets: ['governance'],
      qualifierKeys: ['basis', 'confidence'],
    },
    {
      predicate: 'mitigates',
      family: 'process_compliance',
      lifecycle: 'promotable',
      facets: ['governance'],
    },
    {
      predicate: 'satisfies',
      family: 'process_compliance',
      lifecycle: 'promotable',
      facets: ['governance'],
    },
    {
      predicate: 'governs',
      family: 'process_compliance',
      lifecycle: 'promotable',
      facets: ['governance'],
    },
    {
      predicate: 'remediates',
      family: 'process_compliance',
      lifecycle: 'promotable',
      facets: ['governance', 'lifecycle'],
    },
    {
      predicate: 'framework_contains',
      family: 'process_compliance',
      lifecycle: 'promotable',
      facets: ['governance'],
    },
    {
      predicate: 'finding_from',
      family: 'process_compliance',
      lifecycle: 'promotable',
      facets: ['governance', 'provenance'],
    },
    {
      predicate: 'contains',
      family: 'blueprint_infra',
      lifecycle: 'promotable',
      facets: ['dependency'],
    },
    {
      predicate: 'depends_on',
      family: 'blueprint_infra',
      lifecycle: 'promotable',
      facets: ['dependency'],
      qualifierKeys: ['dependencyType', 'basis', 'validFrom', 'validTo'],
    },
    {
      predicate: 'connects_to',
      family: 'blueprint_infra',
      lifecycle: 'promotable',
      facets: ['dependency'],
    },
    {
      predicate: 'runs_on',
      family: 'blueprint_infra',
      lifecycle: 'promotable',
      facets: ['dependency'],
    },
    {
      predicate: 'data_flow',
      family: 'blueprint_infra',
      lifecycle: 'projection_enabled',
      facets: ['dependency'],
      qualifierKeys: ['operation', 'messageType'],
    },
    {
      predicate: 'message_flow',
      family: 'blueprint_infra',
      lifecycle: 'projection_enabled',
      facets: ['dependency'],
      qualifierKeys: ['messageType'],
    },
    {
      predicate: 'sequence_flow',
      family: 'blueprint_infra',
      lifecycle: 'projection_enabled',
      facets: ['dependency'],
      qualifierKeys: ['condition', 'default'],
    },
    {
      predicate: 'uses_identity',
      family: 'blueprint_infra',
      lifecycle: 'promotable',
      facets: ['identity', 'dependency'],
      qualifierKeys: ['trustDirection'],
      aliases: ['assumes_role'],
    },
    {
      predicate: 'exposes',
      family: 'blueprint_infra',
      lifecycle: 'promotable',
      facets: ['governance', 'dependency'],
    },
    {
      predicate: 'protects',
      family: 'blueprint_infra',
      lifecycle: 'promotable',
      facets: ['governance'],
    },
    {
      predicate: 'causes',
      family: 'strategy_planning',
      lifecycle: 'profile_only',
      facets: ['dependency'],
    },
    {
      predicate: 'informs',
      family: 'strategy_planning',
      lifecycle: 'profile_only',
      facets: ['dependency'],
    },
    {
      predicate: 'implemented_by',
      family: 'strategy_planning',
      lifecycle: 'profile_only',
      facets: ['dependency'],
    },
    {
      predicate: 'mitigated_by',
      family: 'strategy_planning',
      lifecycle: 'profile_only',
      facets: ['governance', 'dependency'],
    },
    {
      predicate: 'milestone_gate',
      family: 'strategy_planning',
      lifecycle: 'profile_only',
      facets: ['lifecycle', 'dependency'],
    },
    {
      predicate: 'scored_against',
      family: 'strategy_planning',
      lifecycle: 'profile_only',
      qualifierKeys: ['score', 'weight', 'basis'],
    },
    {
      predicate: 'outranks',
      family: 'strategy_planning',
      lifecycle: 'profile_only',
      qualifierKeys: ['score', 'basis'],
    },
    {
      predicate: 'becomes_risk',
      family: 'strategy_planning',
      lifecycle: 'profile_only',
      facets: ['lifecycle', 'dependency'],
    },
    {
      predicate: 'extends',
      family: 'software_design',
      lifecycle: 'profile_only',
      facets: ['dependency'],
    },
    {
      predicate: 'implements',
      family: 'software_design',
      lifecycle: 'profile_only',
      facets: ['responsibility', 'dependency'],
    },
    {
      predicate: 'generalizes',
      family: 'software_design',
      lifecycle: 'profile_only',
      facets: ['dependency'],
    },
    {
      predicate: 'composes',
      family: 'software_design',
      lifecycle: 'profile_only',
      facets: ['ownership', 'dependency'],
    },
    {
      predicate: 'aggregates',
      family: 'software_design',
      lifecycle: 'profile_only',
      facets: ['dependency'],
    },
    {
      predicate: 'references',
      family: 'software_design',
      lifecycle: 'profile_only',
      facets: ['dependency'],
      qualifierKeys: ['referenceType'],
    },
  ],
};

const DEFINITIONS_BY_PREDICATE = new Map(
  SHARED_RELATION_PREDICATE_PACK.definitions.map((definition) => [definition.predicate, definition]),
);

const ALIAS_TO_PREDICATE = new Map<string, string>();
for (const definition of SHARED_RELATION_PREDICATE_PACK.definitions) {
  for (const alias of definition.aliases ?? []) {
    ALIAS_TO_PREDICATE.set(alias, definition.predicate);
  }
}

export function listRelationPredicateDefinitions(): readonly RelationPredicateDefinition[] {
  return SHARED_RELATION_PREDICATE_PACK.definitions;
}

export function getRelationPredicateDefinition(
  predicate: string,
): RelationPredicateDefinition | undefined {
  return DEFINITIONS_BY_PREDICATE.get(predicate);
}

export function getCanonicalPredicateForAlias(alias: string): string | undefined {
  return ALIAS_TO_PREDICATE.get(alias);
}

/**
 * Validates a runtime relationship against the single shared ontology contract.
 * Unknown predicates stay profile-only unless the caller is a canonical write
 * boundary. This keeps diagram/profile vocabulary extensible without allowing
 * it to silently enter canonical folder state.
 */
export function validateRelationContract(
  input: RelationContractValidationInput,
): RelationContractValidationResult {
  const issues: RelationContractValidationIssue[] = [];
  const aliasTarget = getCanonicalPredicateForAlias(input.predicate);
  if (aliasTarget && aliasTarget !== input.predicate) {
    issues.push({
      code: 'alias_used_as_canonical',
      message: `Relationship alias '${input.predicate}' must be normalized to '${aliasTarget}'.`,
    });
  }

  const definition = getRelationPredicateDefinition(input.predicate);
  if (!definition) {
    if (input.requireCanonical) {
      issues.push({
        code: 'unregistered_canonical_predicate',
        message: `Relationship predicate '${input.predicate}' is not registered for canonical state.`,
      });
    }
    return { canonical: false, lifecycle: 'profile_only', issues };
  }

  const canonical = definition.lifecycle === 'projection_enabled';
  if (input.requireCanonical && !canonical) {
    issues.push({
      code: 'unregistered_canonical_predicate',
      message: `Relationship predicate '${input.predicate}' has lifecycle '${definition.lifecycle}', not 'projection_enabled'.`,
    });
  }
  if (!canonical) {
    return { canonical: false, lifecycle: definition.lifecycle, definition, issues };
  }

  if (
    input.requireCompleteDefinition
    && (!definition.subjectKinds?.length || !definition.objectKinds?.length || !definition.sourceTypes?.length)
  ) {
    issues.push({
      code: 'incomplete_registry_definition',
      message: `Canonical relationship '${input.predicate}' is missing endpoint or source-authority registration.`,
    });
  }

  const subjectKind = normalizeRelationEndpointKind(input.subjectKind);
  if (subjectKind && definition.subjectKinds && !definition.subjectKinds.includes(subjectKind)) {
    issues.push({
      code: 'invalid_relationship_subject_kind',
      message: `Relationship '${input.predicate}' does not allow subject kind '${subjectKind}'.`,
    });
  }
  const objectKind = normalizeRelationEndpointKind(input.objectKind);
  if (objectKind && definition.objectKinds && !definition.objectKinds.includes(objectKind)) {
    issues.push({
      code: 'invalid_relationship_object_kind',
      message: `Relationship '${input.predicate}' does not allow object kind '${objectKind}'.`,
    });
  }

  const allowedQualifiers = new Set(definition.qualifierKeys ?? []);
  for (const qualifier of new Set(input.qualifierKeys ?? [])) {
    if (!allowedQualifiers.has(qualifier)) {
      issues.push({
        code: 'unsupported_relationship_qualifier',
        message: `Relationship '${input.predicate}' does not register qualifier '${qualifier}'.`,
      });
    }
  }

  if (input.sourceTypes?.length && definition.sourceTypes) {
    const hasSupportedSource = input.sourceTypes.some((sourceType) => definition.sourceTypes!.includes(sourceType));
    if (!hasSupportedSource) {
      issues.push({
        code: 'invalid_relationship_source_type',
        message: `Relationship '${input.predicate}' is not registered for the supplied source authority.`,
      });
    }
  }

  return { canonical, lifecycle: definition.lifecycle, definition, issues };
}

export function normalizeRelationEndpointKind(
  kind: string | null | undefined,
): RelationEndpointKind | null {
  const normalized = kind?.trim().toLowerCase().replace(/^org_/, '');
  if (!normalized) return null;
  if (normalized === 'actor' || normalized === 'employee') return 'person';
  if (normalized === 'role') return 'role_profile';
  return RELATION_ENDPOINT_KINDS.has(normalized as RelationEndpointKind)
    ? normalized as RelationEndpointKind
    : null;
}

const RELATION_ENDPOINT_KINDS = new Set<RelationEndpointKind>([
  'person', 'position', 'department', 'team', 'project', 'role_profile',
  'work_context', 'process', 'activity', 'control', 'system', 'objective',
  'initiative', 'queue', 'skill', 'credential',
]);

export function validateRelationshipPack(
  pack: RelationshipPack,
  options: RelationPackValidationOptions = {},
): readonly RelationPackValidationIssue[] {
  const issues: RelationPackValidationIssue[] = [];
  const strictStatuses = new Set(options.requireRegisteredStatuses ?? STRICT_STATUSES);
  const reportUnregisteredProfileOnly = options.reportUnregisteredProfileOnly ?? false;
  const reportFacetDrift = options.reportFacetDrift ?? true;
  const reportQualifierDrift = options.reportQualifierDrift ?? true;

  for (const subtype of pack.subtypes) {
    const ontology = subtype.ontology;
    if (!ontology?.predicate) continue;
    const predicate = ontology.predicate.trim();
    const status = ontology.status ?? 'profile_only';
    if (!isRelationPredicate(predicate)) {
      issues.push(issue('error', 'invalid_predicate', pack, subtype, predicate, `Invalid relation predicate '${predicate}'.`));
      continue;
    }

    const aliasTarget = getCanonicalPredicateForAlias(predicate);
    if (aliasTarget && aliasTarget !== predicate) {
      issues.push(issue(
        strictStatuses.has(status) ? 'error' : 'warning',
        'alias_used_as_canonical',
        pack,
        subtype,
        predicate,
        `Predicate '${predicate}' is a source/profile alias for '${aliasTarget}', not a canonical predicate.`,
      ));
    }

    const definition = getRelationPredicateDefinition(predicate);
    if (!definition) {
      if (strictStatuses.has(status)) {
        issues.push(issue(
          'error',
          'unregistered_promoted_predicate',
          pack,
          subtype,
          predicate,
          `Predicate '${predicate}' has lifecycle '${status}' but is not registered in ${SHARED_RELATION_PREDICATE_PACK.id}.`,
        ));
      } else if (reportUnregisteredProfileOnly) {
        issues.push(issue(
          'warning',
          'unregistered_profile_predicate',
          pack,
          subtype,
          predicate,
          `Profile-only predicate '${predicate}' is not registered yet.`,
        ));
      }
      continue;
    }

    if (definition.lifecycle === 'deprecated' && status !== 'deprecated') {
      issues.push(issue(
        'error',
        'deprecated_predicate',
        pack,
        subtype,
        predicate,
        `Predicate '${predicate}' is deprecated and cannot be used with lifecycle '${status}'.`,
      ));
    }

    if (
      definition.lifecycle !== 'deprecated' &&
      LIFECYCLE_RANK[status] > LIFECYCLE_RANK[definition.lifecycle]
    ) {
      issues.push(issue(
        'error',
        'lifecycle_exceeds_registry',
        pack,
        subtype,
        predicate,
        `Predicate '${predicate}' is '${definition.lifecycle}' in the registry but '${status}' in the relationship pack.`,
      ));
    }

    if (reportFacetDrift && ontology.facets?.length && definition.facets?.length) {
      const allowed = new Set(definition.facets);
      for (const facet of ontology.facets) {
        if (!allowed.has(facet)) {
          issues.push(issue(
            'warning',
            'facet_outside_registry',
            pack,
            subtype,
            predicate,
            `Facet '${facet}' is not declared for predicate '${predicate}' in ${SHARED_RELATION_PREDICATE_PACK.id}.`,
          ));
        }
      }
    }

    if (reportQualifierDrift && ontology.qualifierKeys?.length) {
      const allowed = new Set(definition.qualifierKeys ?? []);
      for (const qualifier of ontology.qualifierKeys) {
        if (!allowed.has(qualifier)) {
          issues.push(issue(
            strictStatuses.has(status) ? 'error' : 'warning',
            'qualifier_outside_registry',
            pack,
            subtype,
            predicate,
            `Qualifier '${qualifier}' is not declared for predicate '${predicate}' in ${SHARED_RELATION_PREDICATE_PACK.id}.`,
          ));
        }
      }
    }
  }

  return issues;
}

function issue(
  severity: RelationPackValidationSeverity,
  code: RelationPackValidationCode,
  pack: RelationshipPack,
  subtype: RelationshipSubtypeManifest,
  predicate: string,
  message: string,
): RelationPackValidationIssue {
  return {
    severity,
    code,
    packId: pack.meta.id,
    subtypeId: subtype.id,
    predicate,
    message,
  };
}
